import {AnalyticsStateMachine} from '../../analyticsStateMachines/AnalyticsStateMachine';
import {EventDispatcher} from '../../core/EventDispatcher';
import QualityChangeService from '../../core/QualityChangeService';
import {SourceInfoFallbackService} from '../../core/SourceInfoFallbackService';
import {SsaiService} from '../../core/ssai/SsaiService';
import {Event, EventMap} from '../../enums/Event';
import {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import {CustomDataValues} from '../../types/CustomDataValues';
import {DownloadSpeedInfo} from '../../types/DownloadSpeedInfo';
import {DrmPerformanceInfo} from '../../types/DrmPerformanceInfo';
import type {CustomDataValuesEvent} from '../../types/EventData';
import {NoExtraProperties} from '../../types/NoExtraProperties';
import {SegmentInfo} from '../../types/SegmentInfo';
import {defaultStateMachineCallbacks, StateMachineCallbacks} from '../../types/StateMachineCallbacks';
import {logger} from '../../utils/Logger';
import {getCurrentTimestamp} from '../../utils/Utils';
import {WindowEventTracker} from '../../utils/WindowEventTracker';
import {getScreenDimensions} from '../../utils/windowUtils';

import {EventCallbackDispatcher} from './EventCallbackDispatcher';

/**
 * Audio volume below this delta is considered zero.
 */
const volumeDelta = 0.01;

export abstract class InternalAdapter implements EventCallbackDispatcher {
  readonly stateMachineCallbacks: StateMachineCallbacks = {...defaultStateMachineCallbacks};
  readonly qualityChangeService: QualityChangeService = new QualityChangeService();
  readonly ssaiService = new SsaiService();

  protected stateMachine!: AnalyticsStateMachine;
  protected opts: AnalyticsStateMachineOptions;
  protected _onLicenseKeyReceived: EventDispatcher<{licenseKey: string}> = new EventDispatcher();
  protected _onLicenseCallFailed: EventDispatcher<any> = new EventDispatcher();

  protected sourceInfoFallbackService: SourceInfoFallbackService = new SourceInfoFallbackService();
  protected drmPerformanceInfo: DrmPerformanceInfo | undefined = undefined;
  protected abstract get currentTime(): number;

  private _windowEventTracker: WindowEventTracker = new WindowEventTracker();

  get windowEventTracker(): WindowEventTracker {
    return this._windowEventTracker;
  }

  get onLicenseKeyReceived() {
    return this._onLicenseKeyReceived;
  }

  get onLicenseCallFailed() {
    return this._onLicenseCallFailed;
  }

  get downloadSpeedInfo(): DownloadSpeedInfo {
    return {
      segmentsDownloadCount: 0,
      segmentsDownloadSize: 0,
      segmentsDownloadTime: 0,
      avgDownloadSpeed: 0,
      minDownloadSpeed: 0,
      maxDownloadSpeed: 0,
      avgTimeToFirstByte: 0,
    };
  }

  get segments(): SegmentInfo[] {
    return [];
  }

  constructor(opts?: AnalyticsStateMachineOptions) {
    if (!opts) {
      opts = {
        starttime: undefined,
      };
    }
    if (!opts.starttime) {
      opts.starttime = getCurrentTimestamp();
    }

    this.opts = opts;
  }

  release() {
    this.stateMachine.resetIntervals();
    this.stateMachineCallbacks.release();
    this.resetSourceRelatedState();
    this._windowEventTracker.release();
  }

  eventCallback = <StateMachineEvent extends keyof EventMap, StateMachineEventData extends EventMap[StateMachineEvent]>(
    event: StateMachineEvent,
    eventData: NoExtraProperties<EventMap[StateMachineEvent], StateMachineEventData>,
  ) => {
    eventData = eventData || {};
    if (!this.stateMachine) {
      logger.log("Bitmovin Analytics: StateMachine isn't ready yet");
    } else {
      this.stateMachine.callEvent(event, eventData, Date.now());
    }
  };

  getCommonPlaybackInfo() {
    const dimensions = getScreenDimensions();

    return {
      screenHeight: dimensions?.height,
      screenWidth: dimensions?.width,
    };
  }

  clearValues(): void {
    // noop by default
  }

  clearSegments(): void {
    // noop by default
  }

  resetSourceRelatedState(): void {
    this.sourceInfoFallbackService.reset();
    this.drmPerformanceInfo = undefined;
  }

  setCustomData(values: CustomDataValues): void {
    const previousState = this.stateMachine.currentState;
    const eventData: CustomDataValuesEvent = {customDataValues: values, currentTime: this.currentTime};

    if (
      this.stateMachine.currentState &&
      (this.stateMachine.currentState === 'PAUSE' || this.stateMachine.currentState === 'PLAYING')
    ) {
      // If we are in State.PAUSE or State.PLAYING, we want to make "a cut" of the current state
      // to trigger sample sending with current custom data values.
      this.eventCallback(Event.CUSTOM_DATA_CHANGE, {currentTime: this.currentTime});
      // Then we have to call this "previous state" event to return back
      // to the previous state and to set new custom data values.
      // !! here we relay on the fact that we have `PAUSE` and `PLAYING` events with the same name as the states above !!
      this.eventCallback(Event[previousState], eventData);
    }
    // ... else, for any other states, we don't care, and we will just set new custom data values asap, so they will
    // be sent with the upcoming sample
    else {
      this.stateMachineCallbacks.customdatachange(undefined, undefined, eventData);
    }
  }

  protected isAudioMuted(playerIsMuted: boolean, currentVolume: number): boolean {
    return playerIsMuted || currentVolume < volumeDelta;
  }
}
